import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const BannerContainer = styled.div`
  width: 100%;
  height: ${props => props.$height || '200px'};
  position: relative;
  overflow: hidden;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border-radius: ${({theme}) => theme.borderRadius.lg};
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: opacity 0.3s ease;
`;

const BannerPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const BannerInitials = styled.div`
  font-size: ${props => {
    const height = parseInt(props.$height || '200');
    return `${Math.max(height * 0.2, 24)}px`;
  }};
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const BannerName = styled.div`
  font-size: ${props => {
    const height = parseInt(props.$height || '200');
    return `${Math.max(height * 0.08, 14)}px`;
  }};
  font-weight: 500;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.2) 100%
  );
  z-index: 0;
`;

// Generate consistent gradient based on username
const getGradientForUser = (username) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff8a80 0%, #ea8d8d 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)'
  ];
  
  if (!username) return gradients[0];
  
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return gradients[Math.abs(hash) % gradients.length];
};

// Get initials from name/username
const getInitials = (fullName, username) => {
  if (fullName) {
    const names = fullName.trim().split(' ').filter(name => name.length > 0);
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    if (names.length === 1) {
      return names[0].slice(0, 2).toUpperCase();
    }
  }
  
  if (username && username.length > 0) {
    return username[0].toUpperCase();
  }
  
  return '?';
};

/**
 * ProfileBanner Component
 * 
 * Displays a responsive profile banner that automatically selects the optimal
 * JPEG variant based on viewport width using srcSet and the picture element.
 * 
 * Variants available:
 * - b480: 480px width - For mobile devices
 * - b960: 960px width - For tablets
 * - b1920: 1920px width - For desktop
 * - original: Full size - For high-resolution displays
 * 
 * The browser automatically selects the best variant based on viewport width
 * and device pixel ratio.
 * 
 * @example
 * // Basic usage
 * <ProfileBanner user={user} height="200px" />
 * 
 * // With custom fallback
 * <ProfileBanner 
 *   user={user} 
 *   height="250px"
 *   fallbackSrc="/default-banner.jpg"
 * />
 */
const ProfileBanner = ({ 
  user, 
  height = '200px',
  fallbackSrc,
  className,
  style,
  loading = 'lazy',
  onClick,
  alt,
  showPlaceholderText = true,
}) => {
  // Generate srcSet and default src
  // Use user?.banners as dependency to ensure re-render when banners change
  const { srcSet, defaultSrc, hasBanner } = useMemo(() => {
    if (!user) {
      return { srcSet: null, defaultSrc: fallbackSrc, hasBanner: false };
    }
    
    const banners = user.banners;
    
    // Check if we have banner variants
    if (!banners || typeof banners !== 'object') {
      return { srcSet: null, defaultSrc: fallbackSrc, hasBanner: false };
    }
    
    // Helper to check if a URL is valid
    const isValidUrl = (url) => url && typeof url === 'string' && url.length > 0;
    
    const srcSetParts = [];
    
    // Build srcSet with responsive widths
    if (isValidUrl(banners.b480)) srcSetParts.push(`${banners.b480} 480w`);
    if (isValidUrl(banners.b960)) srcSetParts.push(`${banners.b960} 960w`);
    if (isValidUrl(banners.b1920)) srcSetParts.push(`${banners.b1920} 1920w`);
    if (isValidUrl(banners.original)) srcSetParts.push(`${banners.original} 2560w`);
    
    // Use b960 as default (good middle ground)
    const defaultSrc = isValidUrl(banners.b960) ? banners.b960 :
                       isValidUrl(banners.b1920) ? banners.b1920 :
                       isValidUrl(banners.original) ? banners.original :
                       isValidUrl(banners.b480) ? banners.b480 : null;
    
    return {
      srcSet: srcSetParts.length > 0 ? srcSetParts.join(', ') : null,
      defaultSrc,
      hasBanner: !!defaultSrc
    };
  }, [user?.banners, fallbackSrc]);
  
  const handleError = (e) => {
    if (fallbackSrc && e.target.src !== fallbackSrc) {
      e.target.src = fallbackSrc;
    }
  };
  
  const bannerAlt = alt || `${user?.fullName || user?.username || 'User'}'s banner`;
  const initials = getInitials(user?.fullName, user?.username);
  const gradient = getGradientForUser(user?.username);
  const displayName = user?.fullName || user?.username || 'User';
  
  return (
    <BannerContainer 
      $height={height}
      $gradient={gradient}
      className={className} 
      style={style}
      onClick={onClick}
    >
      {hasBanner ? (
        <picture>
          {/* JPEG source with responsive variants */}
          {srcSet && (
            <source
              type="image/jpeg"
              srcSet={srcSet}
              sizes="100vw"
            />
          )}
          <BannerImage
            src={defaultSrc}
            alt={bannerAlt}
            loading={loading}
            onError={handleError}
          />
        </picture>
      ) : (
        <>
          <BannerOverlay />
          {showPlaceholderText && (
            <BannerPlaceholder>
              <BannerInitials $height={height}>{initials}</BannerInitials>
              <BannerName $height={height}>{displayName}</BannerName>
            </BannerPlaceholder>
          )}
        </>
      )}
    </BannerContainer>
  );
};

ProfileBanner.propTypes = {
  /** User object containing banner data */
  user: PropTypes.shape({
    fullName: PropTypes.string,
    username: PropTypes.string,
    /** JPEG variants object */
    banners: PropTypes.shape({
      original: PropTypes.string,
      b1920: PropTypes.string,
      b960: PropTypes.string,
      b480: PropTypes.string,
    }),
  }),
  /** Banner height (e.g., '200px', '250px') */
  height: PropTypes.string,
  /** Fallback image URL if no banner exists */
  fallbackSrc: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
  /** Inline styles */
  style: PropTypes.object,
  /** Image loading strategy */
  loading: PropTypes.oneOf(['lazy', 'eager']),
  /** Click handler */
  onClick: PropTypes.func,
  /** Alt text for the image */
  alt: PropTypes.string,
};

export default ProfileBanner;
